import { createSlice } from "@reduxjs/toolkit"
import mealAPI from "features/meal/mealAPI"
import { createAppAsyncThunk } from "app/hooks"
import { resetStoreAction, DEFAULT_SUGGESTED_MEALS_PAGE_SIZE } from "config"
import { IScanMealPayload, MealState } from "./mealSlice.types"
import dayjs from "dayjs"
import { v4 as uuidv4 } from "uuid"
import { logBrazeEvent } from "libs/braze"
import { capitalizeFirstLetter } from "utils/formater"
import {
  notifyFoodLoggedToApp,
  notifyDailyMealsChangedToApp,
} from "utils/nativeAppHelper"
import {
  ICreateMealPayload,
  IUpdateMealPayload,
} from "model/food/types/mealPayload.types"
import { isAIScanFoodResponse } from "model/food/utils/typeGuards/foodTypeGuards"
import { AIScanFoodOptionType } from "model/food/types"

export const logMeal = createAppAsyncThunk(
  "meal/logMeal",
  async (payload: ICreateMealPayload, { rejectWithValue, getState }) => {
    try {
      const userId = getState().auth.userId
      const date = getState().home.selectedDate
      const isToDay = dayjs(date).isSame(dayjs(), "day")

      if (date && !isToDay) {
        payload.date = date.format("YYYY-MM-DD")
      }

      const response = await mealAPI.logMeal(payload)
      logBrazeEvent({ userId, eventName: "food_manual_input_completed" })
      notifyFoodLoggedToApp()
      notifyDailyMealsChangedToApp()
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const logScannedFoods = createAppAsyncThunk(
  "meal/logScannedFoods",
  async (payload: ICreateMealPayload[], { rejectWithValue, getState }) => {
    try {
      const userId = getState().auth.userId
      const date = getState().home.selectedDate
      const isToDay = dayjs(date).isSame(dayjs(), "day")
      if (date && !isToDay) {
        payload = payload.map((item) => ({
          ...item,
          date: date.format("YYYY-MM-DD"),
        }))
      }
      const requests = payload.map((item) => mealAPI.logMeal(item))
      const responses = await Promise.all(requests)

      responses.forEach(() => {
        logBrazeEvent({ userId, eventName: "food_ai_input_completed" })
        notifyFoodLoggedToApp()
      })
      notifyDailyMealsChangedToApp()
      return responses
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const updateMeal = createAppAsyncThunk(
  "meal/updateMeal",
  async (payload: IUpdateMealPayload, { rejectWithValue }) => {
    try {
      const response = await mealAPI.updateMeal(payload)
      notifyDailyMealsChangedToApp()
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const scanMeal = createAppAsyncThunk(
  "meal/scanMeal",
  async (payload: IScanMealPayload, { rejectWithValue }) => {
    try {
      // Step 1: Initiate scan
      const initResponse = await mealAPI.scan(payload)

      // Step 2: Handle immediate completion
      if (initResponse.status === "SUCCEEDED") {
        return initResponse.foods || []
      }

      if (initResponse.status === "FAILED") {
        return []
      }

      // Step 3: Poll if status is "STARTED"
      const POLLING_INTERVAL = 3000 // 3 seconds
      const MAX_ATTEMPTS = 40 // 120 seconds total

      let attempts = 0
      while (attempts < MAX_ATTEMPTS) {
        const statusResponse = await mealAPI.getScanStatus(initResponse.id)

        if (statusResponse.status === "SUCCEEDED") {
          return statusResponse.foods || []
        }

        // NOT FOUND
        if (statusResponse.status === "FAILED") {
          return []
        }

        // Still processing, wait before next poll
        await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL))
        attempts++
      }

      // Timeout reached
      throw new Error("Scan timeout: exceeded 100 seconds")
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

let getSuggestedMealsAbortController: AbortController | null = null

export const getSuggestedMeals = createAppAsyncThunk(
  "meal/getSuggestedMeals",
  async ({ page }: { page: number }, { rejectWithValue }) => {
    const isFirstPage = page === 1

    if (getSuggestedMealsAbortController && isFirstPage) {
      getSuggestedMealsAbortController.abort()
    }

    const localController = new AbortController()
    getSuggestedMealsAbortController = localController

    try {
      const meals = await mealAPI.getSuggestedMeals({
        page,
        signal: localController.signal,
      })
      return meals
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (getSuggestedMealsAbortController === localController) {
        getSuggestedMealsAbortController = null
      }
    }
  },
)

const onImageLoaded = (src: string) =>
  new Promise((resolve) => {
    if (!src) resolve(true)
    const img = new Image()
    img.src = src

    img.onload = function () {
      resolve(true)
    }
  })

export const processImage = createAppAsyncThunk(
  "meal/processImage",
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await mealAPI.processImage(file)
      await onImageLoaded(response.url)

      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const processScannedFoodImage = createAppAsyncThunk(
  "meal/processScannedFoodImage",
  async (
    { file, foodId }: { file: File; foodId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await mealAPI.processImage(file)
      await onImageLoaded(response.url)

      return { ...response, foodId }
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const getMealDetails = createAppAsyncThunk(
  "meal/getMealDetails",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await mealAPI.getMealDetails(id)
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const getDailyScanProgress = createAppAsyncThunk(
  "meal/getDailyScanProgress",
  async (_, { rejectWithValue }) => {
    try {
      const response = await mealAPI.getDailyScanProgress()
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const searchFoodByBarcode = createAppAsyncThunk(
  "meal/searchFoodByBarcode",
  async (barcode: string, { rejectWithValue }) => {
    try {
      const { foods } = await mealAPI.searchFoodByBarcode(barcode)
      return foods[0]
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

const initialState: MealState = {
  searchFoodByBarcodeLoading: false,
  searchFoodByBarcodeFailed: undefined,
  searchFoodByBarcodeResult: undefined,
  noFoodFoundFromBarcode: false,

  scannedFoodUploadedImageDict: {},
  processScannedFoodImageLoading: false,
  processScannedFoodImageFailed: undefined,

  logScannedFoodsLoading: false,
  logScannedFoodsFailed: undefined,
  logScannedFoodsSuccess: undefined,

  updateMealLoading: false,
  updateMealFailed: undefined,
  updateMealSuccess: undefined,

  mealDetailsBackPath: undefined,

  logMealLoading: false,
  logMealSuccess: undefined,
  logMealFailed: undefined,

  suggestedMeals: [],
  getSuggestedMealsLoading: false,
  getSuggestedMealsFailed: undefined,
  isNoSuggestedMeals: false,
  suggestedMealsPage: 0,
  hasMoreSuggestedMeals: true,

  dailyScanProgress: {
    remainingScan: 0,
    enabled: true,
  },

  getDailyScanProgressLoading: false,
  getDailyScanProgressFailed: undefined,

  scanMealResults: [],
  scanMealLoading: false,
  scanMealFailed: undefined,
  imageBase64Encoded: "",
  convertImageLoading: false,
  step: "take-photo",

  scanningImageUrl: undefined,

  file: undefined,

  imageUrl: "",
  imageId: "",
  processImageLoading: false,
  processImageFailed: undefined,
  displayImageType: "base64",

  mealDetails: undefined,
  getMealDetailsLoading: false,
  getMealDetailsFailed: undefined,
}

export const mealSlice = createSlice({
  name: "meal",
  initialState,
  reducers: {
    revertScannedFOodToOriginImageState(state, { payload }) {
      state.scannedFoodUploadedImageDict[payload.foodId] =
        payload.originImageState
    },
    uploadScannedFoodImage(state, { payload }) {
      state.scannedFoodUploadedImageDict[payload.foodId] = {
        imageFile: payload.image,
        base64: payload.base64,
      }
    },

    removeScannedFoodUploadedImage(state, { payload }) {
      state.scannedFoodUploadedImageDict[payload.foodId] = {}
    },

    setMealDetailsBackPath(state, { payload }) {
      state.mealDetailsBackPath = payload
    },

    setMealDetails(state, { payload }) {
      state.mealDetails = payload
    },

    uploadImage(state, { payload }) {
      state.step = "preview-image"

      state.imageBase64Encoded = payload.base64
      state.file = payload.file
      state.displayImageType = "base64"
      state.imageUrl = ""
      state.imageId = ""
    },

    setStep(state, { payload }) {
      if (payload === "take-photo") {
        return initialState
      }

      state.step = payload
    },

    removeUploadedImage(state) {
      state.file = undefined
      state.imageBase64Encoded = ""
      state.imageId = ""
      state.imageUrl = ""
    },

    clearNoFoodFromBarcode(state) {
      state.noFoodFoundFromBarcode = false
    },

    resetSuggestedMeals(state) {
      state.suggestedMeals = []
      state.getSuggestedMealsLoading = false
      state.getSuggestedMealsFailed = undefined
      state.suggestedMealsPage = 0
      state.hasMoreSuggestedMeals = true
    },

    resetMealState() {
      return initialState
    },
  },
  extraReducers(builder) {
    builder
      .addCase(searchFoodByBarcode.pending, (state) => {
        state.searchFoodByBarcodeLoading = true
        state.searchFoodByBarcodeFailed = undefined
        state.searchFoodByBarcodeResult = undefined
        state.noFoodFoundFromBarcode = false
      })
      .addCase(searchFoodByBarcode.fulfilled, (state, { payload }) => {
        state.searchFoodByBarcodeLoading = false
        state.searchFoodByBarcodeResult = payload
        state.noFoodFoundFromBarcode = payload === undefined
      })
      .addCase(searchFoodByBarcode.rejected, (state, { payload }) => {
        state.searchFoodByBarcodeLoading = false
        state.searchFoodByBarcodeFailed = payload
      })

      .addCase(processScannedFoodImage.pending, (state) => {
        state.processScannedFoodImageLoading = true
      })
      .addCase(processScannedFoodImage.fulfilled, (state, { payload }) => {
        state.processScannedFoodImageLoading = false
        state.scannedFoodUploadedImageDict[payload.foodId] = {
          imageUrl: payload.url,
          imageId: payload.id,
          previewImageUrl: payload.url,
        }
      })

      .addCase(processScannedFoodImage.rejected, (state, { payload }) => {
        state.processScannedFoodImageLoading = false
        state.processScannedFoodImageFailed = payload
      })

      .addCase(logScannedFoods.pending, (state) => {
        state.logScannedFoodsLoading = true
      })
      .addCase(logScannedFoods.fulfilled, (state, { payload }) => {
        state.logScannedFoodsLoading = false
        state.logScannedFoodsSuccess = payload
      })

      .addCase(logScannedFoods.rejected, (state, { payload }) => {
        state.logScannedFoodsLoading = false
        state.logScannedFoodsFailed = payload
      })

      .addCase(updateMeal.pending, (state) => {
        state.updateMealLoading = true
      })
      .addCase(updateMeal.fulfilled, (state, { payload }) => {
        state.updateMealLoading = false
        state.updateMealSuccess = payload
        state.mealDetails = payload
      })
      .addCase(updateMeal.rejected, (state, { payload }) => {
        state.updateMealLoading = false
        state.updateMealFailed = payload
      })

      .addCase(logMeal.pending, (state) => {
        state.logMealLoading = true
      })
      .addCase(logMeal.fulfilled, (state, { payload }) => {
        state.logMealLoading = false
        state.logMealSuccess = payload
      })
      .addCase(logMeal.rejected, (state, { payload }) => {
        state.logMealLoading = false
        state.logMealFailed = payload
      })

      .addCase(getSuggestedMeals.pending, (state, { meta }) => {
        state.getSuggestedMealsLoading = true
        state.suggestedMealsPage = meta?.arg?.page || 0
      })
      .addCase(getSuggestedMeals.fulfilled, (state, { payload }) => {
        state.getSuggestedMealsLoading = false

        const isFirstPage = state.suggestedMealsPage === 1
        const currentMeals = isFirstPage ? [] : state.suggestedMeals

        state.suggestedMeals = [...currentMeals, ...payload]
        state.isNoSuggestedMeals = state.suggestedMeals.length === 0
        state.hasMoreSuggestedMeals =
          payload.length >= DEFAULT_SUGGESTED_MEALS_PAGE_SIZE
      })
      .addCase(getSuggestedMeals.rejected, (state, { payload }) => {
        if ((payload as any)?.aborted) return
        state.getSuggestedMealsLoading = false
        state.getSuggestedMealsFailed = payload
      })

      .addCase(scanMeal.pending, (state) => {
        state.scanMealLoading = true
        state.step = "scan-animation"

        const image =
          state.displayImageType && state.displayImageType === "base64"
            ? state.imageBase64Encoded
            : state.imageUrl

        state.scanningImageUrl = image
      })
      .addCase(scanMeal.fulfilled, (state, { payload: scannedFoods }) => {
        if (scannedFoods.length === 0) {
          state.step = "not-found"
          return
        }

        state.scanMealResults = scannedFoods.map((food) => {
          if (isAIScanFoodResponse(food)) {
            const foodOption: AIScanFoodOptionType = {
              ...food,
              name: capitalizeFirstLetter(food.name),
              isSelected: true,
              uuid: uuidv4(),
              ingredients: food.ingredients.map((ingredient) => {
                const ingredientId = uuidv4()
                return {
                  ...ingredient,
                  id: ingredientId,
                  uuid: ingredientId,
                }
              }),
            }
            return foodOption
          }

          const foodOption: AIScanFoodOptionType = {
            ...food,
            name: capitalizeFirstLetter(food.name),
            isSelected: true,
            uuid: uuidv4(),
          }

          return foodOption
        })

        state.scannedFoodUploadedImageDict = state.scanMealResults.reduce(
          (acc, food) => {
            return {
              ...acc,
              [food.id]: {
                imageId: food.imageId,
                imageUrl: food.imageUrl,
                previewImageUrl: food.previewImageUrl,
              },
            }
          },
          {},
        )

        state.scanMealLoading = false
        state.imageId = ""
        state.imageUrl = ""
        state.imageBase64Encoded = ""
        state.file = undefined
        state.step = "scan-result"
        state.processImageFailed = undefined
        state.scanMealFailed = undefined
      })
      .addCase(scanMeal.rejected, (state, { payload }) => {
        state.scanMealLoading = false
        state.scanMealFailed = payload
        state.step = "scan-failed"
        state.scanningImageUrl = undefined
      })

      .addCase(processImage.pending, (state) => {
        state.processImageLoading = true
        state.processImageFailed = undefined
        state.imageUrl = ""
        state.imageId = ""
        state.imageBase64Encoded = ""
        state.displayImageType = "url"
        state.step = "preview-image"
      })

      .addCase(processImage.fulfilled, (state, { payload }) => {
        state.imageUrl = payload.url
        state.imageId = payload.id
        state.processImageLoading = false
      })

      .addCase(processImage.rejected, (state, { payload }) => {
        state.processImageLoading = false
        state.imageUrl = ""
        state.imageId = ""
        state.processImageFailed = payload
      })

      .addCase(getMealDetails.pending, (state) => {
        state.getMealDetailsLoading = true
      })
      .addCase(getMealDetails.fulfilled, (state, { payload }) => {
        state.getMealDetailsLoading = false
        state.mealDetails = payload
      })
      .addCase(getMealDetails.rejected, (state, { payload }) => {
        state.getMealDetailsLoading = false
        state.getMealDetailsFailed = payload
      })

      .addCase(getDailyScanProgress.pending, (state) => {
        state.getDailyScanProgressLoading = true
        state.getDailyScanProgressFailed = undefined
      })
      .addCase(getDailyScanProgress.fulfilled, (state, { payload }) => {
        state.getDailyScanProgressLoading = false
        state.dailyScanProgress = payload
      })
      .addCase(getDailyScanProgress.rejected, (state, { payload }) => {
        state.getDailyScanProgressLoading = false
        state.getDailyScanProgressFailed = payload
        state.dailyScanProgress = undefined
      })

      .addCase(resetStoreAction, () => {
        return initialState
      })
  },
})

export const {
  uploadImage,
  setStep,
  resetMealState,
  setMealDetails,
  removeUploadedImage,
  setMealDetailsBackPath,
  uploadScannedFoodImage,
  removeScannedFoodUploadedImage,
  revertScannedFOodToOriginImageState,
  clearNoFoodFromBarcode,
  resetSuggestedMeals,
} = mealSlice.actions

export default mealSlice.reducer
